import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, FileUp, Pencil, Radar } from "lucide-react";
import { useApi, errMsg, downloadBlob } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { fmtDate, isStaff } from "@/lib/format";
import { PageHero } from "@/components/PageHero";
import RouteMap from "@/components/RouteMap";
import { TrackingLive } from "@/components/FlightProgress";
import TripSwipeNav from "@/components/TripSwipeNav";
import { MoveBadge, code, lateText, nextLeg, shownStatus, zoned } from "@/components/TrackingBits";
import { LegDetailsDialog, MoveDialog } from "@/components/TrackingDialogs";
import { TripBriefButton } from "@/components/TripBriefButton";
import { CalendarInviteButton } from "@/components/CalendarInviteButton";
import { ItineraryReviewDialog, ItineraryStatus, readState } from "@/components/ItineraryReview";
import { OperatorDialog } from "@/components/OperatorDialog";
import { PassengerList } from "@/components/PassengerList";

const ACTIONS = { scheduled: ["in_position", "departed", "cancelled"], delayed: ["in_position", "departed", "cancelled", "scheduled"], departed: ["landed", "eta"],
  diverted: ["landed", "eta"], landed: ["scheduled"], cancelled: ["scheduled"] };
const ACTION_LABEL = { departed: "Departed", landed: "Landed", delayed: "Delayed", diverted: "Diverted", cancelled: "Cancel leg", scheduled: "Reset", eta: "Update ETA", in_position: "Plane in position" };

const Info = ({ label, children, testId }) => (
  <div><dt className="text-[0.58rem] tracking-[0.22em] uppercase text-zinc-500">{label}</dt><dd className="text-sm text-zinc-100 mt-1 break-words" data-testid={testId}>{children || "—"}</dd></div>
);
const fboText = (f) => [f.name, f.address, f.phone].filter(Boolean).join(" · ");

function LegCard({ t, lg, i, staff, onMove, onEdit }) {
  const { dep_zone: dz, arr_zone: az } = lg.progress || {};
  const early = lg.status !== "departed" && lg.date && lg.date > new Date().toLocaleDateString("en-CA");
  const time = lg.departed_at ? `Departed ${zoned(lg.departed_at, dz)}${lg.eta && lg.status !== "landed" ? ` · ETA ${zoned(lg.eta, az)}` : ""}` : lg.delayed_to ? `Now ${zoned(lg.delayed_to, dz)}` : zoned(lg.time, dz) || "Time TBA";
  return (
    <article className="lux-card p-5 sm:p-7" data-testid={`tracking-leg-${i}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="eyebrow mb-1" data-testid={`tracking-leg-${i}-date`}>Leg {i + 1} · {fmtDate(lg.date)}</p>
          <p className="font-serif-display text-3xl text-white">{code(lg.from_airport)} <span className="text-[#C6A55C]">→</span> {code(lg.diverted_to || lg.to_airport)}</p>
          <p className="text-sm text-zinc-300 mt-1" data-testid={`tracking-leg-${i}-time`}>{time}{lg.landed_at ? ` · Landed ${zoned(lg.landed_at, az)}` : ""}</p>
          {lg.aircraft && <p className="text-xs text-[#9DB4EA] mt-1" data-testid={`tracking-leg-${i}-aircraft`}>{lg.aircraft.aircraft_type} · {lg.aircraft.tail_number || "Tail TBA"}{lg.aircraft.operator_name ? ` · ${lg.aircraft.operator_name}` : ""}</p>}
          {lg.in_position && ["scheduled", "delayed"].includes(lg.status) && (
            <p className="text-xs text-sky-700 mt-1" data-testid={`tracking-leg-${i}-in-position`}>In position {zoned(lg.in_position.at, dz)}{lg.in_position.note ? ` · ${lg.in_position.note}` : ""}</p>
          )}
          {lg.progress?.landing_overdue_min != null && (
            <p className="text-xs font-semibold text-rose-600 mt-1" data-testid={`tracking-leg-${i}-landing-overdue`}>Landing overdue · ETA passed {lateText(lg.progress.landing_overdue_min)} ago. Mark Landed or update the ETA.</p>
          )}
          {lg.diverted_to && <p className="text-xs text-rose-600 mt-1">Diverted to {lg.diverted_to} (planned {code(lg.to_airport)})</p>}
          {lg.track_url && (
            <a href={lg.track_url} target="_blank" rel="noopener noreferrer" className="frame-btn !py-2.5 !px-4 mt-4 !border-[#C6A55C]/60" data-testid={`tracking-leg-${i}-track-live-link`}>
              <Radar size={13} className="text-[#C6A55C]" /> Track live
            </a>
          )}
        </div>
        <MoveBadge status={shownStatus(lg)} testId={`tracking-leg-${i}-status`} />
      </div>
      {staff && (
        <div className="flex flex-wrap gap-2 mt-5">
          {ACTIONS[lg.status].map((s) => (
            <button key={s} type="button" onClick={() => onMove({ i, status: s })} data-testid={`tracking-leg-${i}-${s}-btn`}
              disabled={s === "departed" && early} title={s === "departed" && early ? `Available on ${fmtDate(lg.date)}` : undefined}
              className={`frame-btn !py-3 !px-5 ${["departed", "landed"].includes(s) ? "gold" : ""}`}>{ACTION_LABEL[s]}</button>
          ))}
          <button type="button" className="frame-btn !py-3 !px-5" onClick={() => onEdit(i)} data-testid={`tracking-leg-${i}-edit-btn`}><Pencil size={12} /> Trip details</button>
          {early && ACTIONS[lg.status].includes("departed") && <p className="basis-full text-[11px] text-zinc-500" data-testid={`tracking-leg-${i}-depart-hint`}>Departed can be marked from {fmtDate(lg.date)}.</p>}
        </div>
      )}
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 mt-6 border-t border-white/[0.08] pt-5">
        <Info label="Passengers" testId={`tracking-leg-${i}-pax`}>{lg.passengers.length ? <span className="whitespace-pre-line">{lg.passengers.join("\n")}</span> : `${lg.pax || "—"} pax`}</Info>
        <Info label="Departure FBO" testId={`tracking-leg-${i}-dep-fbo`}>{fboText(lg.dep_fbo)}</Info>
        <Info label="Arrival FBO" testId={`tracking-leg-${i}-arr-fbo`}>{fboText(lg.arr_fbo)}</Info>
        <Info label="Catering">{lg.catering}</Info>
        <Info label="Ground transport">{lg.ground}</Info>
      </dl>
    </article>
  );
}

function LegList({ t, staff, showLanded, onToggle, onMove, onEdit }) {
  const landed = t.legs.filter((lg) => lg.status === "landed").length;
  const hide = landed > 0 && landed < t.legs.length && !showLanded;
  return (
    <div className="space-y-4 mt-8">
      {landed > 0 && landed < t.legs.length && (
        <button type="button" onClick={onToggle} className="text-[0.62rem] tracking-[0.2em] uppercase text-zinc-500 hover:text-[#C6A55C]" data-testid="toggle-landed-legs-btn">
          {showLanded ? "Hide landed legs" : `Show ${landed} landed leg${landed === 1 ? "" : "s"}`}
        </button>
      )}
      {t.legs.map((lg, i) => (hide && lg.status === "landed" ? null : <LegCard key={i} t={t} lg={lg} i={i} staff={staff} onMove={onMove} onEdit={onEdit} />))}
    </div>
  );
}

function TripSheet({ t, staff, onSaved }) {
  const api = useApi();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("The itinerary must be 20 MB or smaller"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/tracking/${t.id}/trip-sheet`, fd);
      toast.success("Itinerary uploaded. Reading the details…");
      onSaved(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="trip-sheet">
      {t.trip_sheet ? (
        <button type="button" className="frame-btn" data-testid="trip-sheet-download-btn"
          onClick={() => downloadBlob(api, `/tracking/${t.id}/trip-sheet`, `Itinerary-${t.trip_ref.replace(/[^A-Za-z0-9]+/g, "-")}.${{ "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" }[t.trip_sheet.mime] || "pdf"}`).catch(() => toast.error("Download failed"))}>
          <Download size={13} /> Itinerary
        </button>
      ) : <span className="text-xs text-zinc-400" data-testid="trip-sheet-none">No itinerary yet</span>}
      {staff && (
        <>
          <input ref={input} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="trip-sheet-input" />
          <button type="button" className="frame-btn gold" disabled={busy} onClick={() => input.current.click()} data-testid="trip-sheet-upload-btn">
            <FileUp size={13} /> {busy ? "Uploading…" : t.trip_sheet ? "Replace itinerary" : "Upload operator itinerary"}
          </button>
        </>
      )}
    </div>
  );
}

export default function TrackingDetail() {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { me } = useMe();
  const staff = isStaff(me);
  const [t, setT] = useState(null);
  const [failed, setFailed] = useState(false);
  const [move, setMove] = useState(null);
  const [edit, setEdit] = useState(null);
  const [review, setReview] = useState(false);
  const [opEdit, setOpEdit] = useState(false);
  const [showLanded, setShowLanded] = useState(false);
  const prevRead = useRef(null);
  const load = useCallback(() => api.get(`/tracking/${id}`).then((r) => setT(r.data)).catch(() => setFailed(true)), [api, id]);
  useEffect(() => { setT(null); setFailed(false); load(); }, [load]);
  const read = t ? readState(t) : null;
  useEffect(() => {
    if (prevRead.current === "running" && read === "done" && staff) setReview(true);
    prevRead.current = read;
    if (read !== "running") return undefined;
    const timer = setInterval(load, 3000);
    return () => clearInterval(timer);
  }, [read, load, staff]);
  if (failed) return (
    <div className="py-24 text-center" data-testid="tracking-not-found">
      <p className="font-serif-display text-4xl text-white">Tracked trip not found</p>
      <Link to="/tracking" className="frame-btn mt-8" data-testid="tracking-not-found-back">Back to flight tracking</Link>
    </div>
  );
  if (!t) return <div className="py-24 grid place-items-center"><div className="jet-loader" /></div>;
  const a = t.aircraft;
  return (
    <div key={id} data-testid="tracking-detail-page">
      <PageHero testId="tracking-detail-hero">
        <div className="flex items-center justify-between gap-3 mb-8">
          <Link to="/tracking" className="inline-flex items-center gap-2 text-[0.65rem] tracking-[0.22em] uppercase text-zinc-300 hover:text-white" data-testid="back-to-tracking-link"><ArrowLeft size={13} /> Flight tracking</Link>
          <TripSwipeNav id={id} kind="tracking" />
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <MoveBadge dark status={t.status === "completed" ? "landed" : nextLeg(t).status} testId="tracking-overall-status" />
          {t.status === "completed" && <span className="text-[0.6rem] tracking-[0.2em] uppercase text-emerald-300" data-testid="tracking-completed">Trip completed</span>}
        </div>
        <h1 className="font-serif-display text-3xl sm:text-5xl text-white uppercase">{t.trip_ref}</h1>
        <p className="text-zinc-300 mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span data-testid="tracking-aircraft">{a.aircraft_type} · {a.tail_number || "Tail TBA"} · {a.operator_name}</span>
          {staff && <button type="button" onClick={() => setOpEdit(true)} className="inline-flex items-center gap-1 text-[0.62rem] tracking-[0.2em] uppercase text-[#C6A55C] hover:text-white" data-testid="operator-edit-btn"><Pencil size={11} /> Edit operator</button>}
        </p>
        {(a.operator_trip_id || a.operator_address || a.operator_phone) && (
          <p className="text-xs text-zinc-400 mt-2 break-words" data-testid="tracking-operator-details">
            {[a.operator_trip_id && `Operator trip ID ${a.operator_trip_id}`, a.operator_address, a.operator_phone].filter(Boolean).join(" · ")}
          </p>
        )}
        <div className="mt-6 flex flex-wrap gap-3 items-start">
          <TripSheet t={t} staff={staff} onSaved={setT} />
          {staff && <ItineraryStatus t={t} onSaved={setT} onReview={() => setReview(true)} />}
          {staff && <TripBriefButton t={t} onSaved={setT} />}
          {staff && <CalendarInviteButton t={t} onSaved={setT} />}
          <Link to={`/trips/${t.trip_id}`} className="frame-btn" data-testid="tracking-open-trip-link">Open trip request</Link>
        </div>
      </PageHero>
      <TrackingLive t={t} RouteMap={RouteMap} />
      <div className="mt-8"><PassengerList tripId={t.trip_id} legs={t.legs} staff={staff} onSaved={load} /></div>
      <LegList t={t} staff={staff} showLanded={showLanded} onToggle={() => setShowLanded((s) => !s)} onMove={setMove} onEdit={setEdit} />
      <MoveDialog move={move} t={t} onClose={() => setMove(null)} onSaved={(d) => {
        if (d.trashed) { toast.success("Final leg landed. Trip completed and moved to Trash."); navigate("/tracking"); return; }
        setT(d);
      }} />
      <LegDetailsDialog i={edit} t={t} onClose={() => setEdit(null)} onSaved={setT} />
      {staff && <ItineraryReviewDialog open={review} t={t} onClose={() => setReview(false)} onSaved={setT} />}
      {staff && <OperatorDialog open={opEdit} t={t} onClose={() => setOpEdit(false)} onSaved={setT} />}
    </div>
  );
}
